import { formatPhoneNumber } from "@toss/utils";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useBlockLayout, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import { getContractNumbers } from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";
import { BaseInput, BasePagination, BaseRadio } from "src/components";
import BaseEmail from "src/components/BaseEmail";
import useApiLoading from "src/hooks/useApiLoading";

/**
 * 테이블 컬럼 정의
 * 선택, id, 회원번호, 휴대폰번호, 이메일 정보를 포함
 */
const columnHeader: any = [
  {
    Header: "선택",
    accessor: "isSelected",
    // sticky: "left",
    width: 140,
    Cell: ({ rows, row, setSelectedMember }: any) => {
      // 라디오 버튼 선택 시 호출되는 콜백 함수
      const changeTrigger = useCallback(() => {
        console.log("row.original.id", row.original);
        setSelectedMember(row.original);
      }, [row.original, setSelectedMember]);

      return (
        <div>
          <BaseRadio id={`selector_${row.original.index}`} name={"isSelected"} onChange={changeTrigger} />
        </div>
      );
    },
  },
  {
    Header: "id",
    accessor: "contractMemberId",
    // sticky: "left",
    width: 140,
  },
  {
    Header: "회원번호",
    accessor: "memberNo",
    width: 220,
  },
  {
    Header: "휴대폰번호",
    accessor: "inviteMobileNumber",
    width: 220,
    Cell: ({ value }: any) => {
      return <p>{formatPhoneNumber(value)}</p>;
    },
  },
  {
    Header: "이메일",
    accessor: "email",
    width: 220,
    Cell: ({ value }: any) => {
      return (
        <div>
          <BaseEmail value={value} />
        </div>
      );
    },
  },
];

/**
 * 계약 회원 조회 모달의 props 인터페이스
 * @param contractManageId 계약 관리 ID
 * @param isOpen 모달 표시 여부
 * @param onClick 선택 콜백 함수
 * @param onClose 모달 닫기 콜백 함수
 */
type Props = {
  contractManageId?: number;
  isOpen: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
};

/**
 * 계약 회원 조회 모달 컴포넌트
 * 계약 회원을 조회하고 선택할 수 있는 모달 컴포넌트입니다.
 * 회원번호 또는 휴대폰 번호로 회원을 검색할 수 있습니다.
 * @param props 컴포넌트 props
 */
const ContractMemberFind = (props: Props) => {
  const { isApiLoading } = useApiLoading();
  // 회원 목록 데이터
  const [data, setData] = useState<any>([]);
  // 선택된 회원 정보
  const [selectedMember, setSelectedMember] = useState<any>();
  // 페이지 메타 정보
  const [pageMeta, setPageMeta] = useState<any>();
  // 검색어
  const [searchValue, setSearchValue] = useState("");
  // API 요청 파라미터
  const [params, setParams] = useState<any>({
    page: 0,
    size: 20,
    contractManageId: props.contractManageId,
    searchValue: "",
  });

  // 테이블 설정
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
    {
      columns: columnHeader,
      data: data,
      setSelectedMember,
    },
    useBlockLayout,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useSticky,
    useRowSelect,
  );

  // 테이블 전체 너비 계산
  const tableWidth = useMemo(() => {
    let totalWidth = 0;
    headerGroups.forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => (totalWidth += Number(header?.width || 0)));
    });
    return totalWidth;
  }, [headerGroups]);

  // 계약 회원 조회 API 호출
  const { executeAsync: executeAsyncAll } = useApiOperation(getContractNumbers);

  /**
   * 회원 선택 처리 함수
   */
  const onClickData = useCallback(() => {
    if (props.onClick) {
      props.onClick(selectedMember);
    }
  }, [props, selectedMember]);

  /**
   * 모달 닫기 처리 함수
   */
  const closeData = useCallback(async () => {
    if (props.onClose) props.onClose(selectedMember);
  }, [props]);

  /**
   * 계약 회원 목록 조회 함수
   * @param params API 요청 파라미터
   */
  const callContractUsers = useCallback(
    async (params: any) => {
      console.log("params", params);
      const response: any = await executeAsyncAll(params);

      if (response?.data?.data && response?.data?.data.length > 0) {
        for (let i = 0; i < response?.data?.data?.length; i++) {
          response.data.data[i] = Object.assign(response.data.data[i], {
            isSelected: false,
            index: i,
          });
        }
      }
      setData(response?.data?.data);
      setPageMeta(response?.data?.meta?.pageMeta);
    },
    [executeAsyncAll],
  );

  // 모달이 열릴 때 계약 회원 목록 조회
  useEffect(() => {
    if (props.isOpen) {
      let prs = _.cloneDeep(params);
      setSearchValue("");
      prs.contractManageId = props.contractManageId;
      prs.searchValue = "";
      prs.sort = {
        orders: [{ property: "id", direction: "DESC" }],
      };
      callContractUsers(prs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  return (
    <>
      <section className="base-abstract-modal__title">
        <div className="flex-row flex-center-between w-100">
          <h1>이용자</h1>

          <div className="minmax260 text-left">
            <BaseInput
              type="text"
              value={searchValue || ""}
              placeholder="회원번호 또는 휴대폰 번호 검색"
              onChange={(value: string) => {
                setSearchValue(value);

                setParams({ ...params, ...{ searchValue: value } });
              }}
              onKeyUp={() => {
                setParams({ ...params, ...{ searchValue }, ...{ page: 0 } });
                callContractUsers({ ...params, ...{ searchValue }, ...{ page: 0 } });
              }}
              onSearchClick={() => {
                setParams({ ...params, ...{ searchValue }, ...{ page: 0 } });
                callContractUsers({ ...params, ...{ searchValue }, ...{ page: 0 } });
              }}
              onClearClick={() => {
                setSearchValue("");
                setParams({ ...params, ...{ searchValue: "" }, ...{ page: 0 } });
              }}
            />
          </div>
        </div>
      </section>
      {/* 테이블 */}
      <section className="base-abstract-modal__contents">
        <div {...getTableProps()} className="base-table sticky  view-data-table px30">
          <div className="header">
            {headerGroups.map((headerGroup) => (
              <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                {headerGroup.headers.map((header) => {
                  return (
                    <div {...header.getHeaderProps()} className="base-table__th">
                      {header.render("Header")}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div {...getTableBodyProps()} className="body">
            {rows.map((row: any) => {
              prepareRow(row);
              return (
                <div {...row.getRowProps()} className={`base-table__tr ${row.values.floor ? "bg-gray100" : ""}`}>
                  {row.cells.map((cell: any) => {
                    return (
                      <div {...cell.getCellProps()} className="base-table__td">
                        {cell.render("Cell")}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <div>
              {rows.length === 0 && (
                <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                  <div className="base-table__td w-100 text-center">
                    <div className="w-100">{!isApiLoading() && <span>데이터가 없습니다.</span>}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <section className="px30">
        <BasePagination
          pageIndex={params.page || 0}
          totalPages={pageMeta?.totalPages || 0}
          currentSize={Number(params.size) || 20}
          sizeOption={(sizeValue) => {
            setParams({ ...params, size: sizeValue });
            callContractUsers({ ...params, size: sizeValue });
          }}
          totalElements={pageMeta?.totalElements || 0}
          goPage={(page: number) => {
            setParams({ ...params, ...{ page } });
            callContractUsers({ ...params, ...{ page } });
          }}
          children={
            <div className="flex-center">
              <button className="base-btn color-white mr10" onClick={() => closeData()}>
                취소
              </button>
              <button className="base-btn" onClick={() => onClickData()}>
                선택
              </button>
            </div>
          }
        />
      </section>
    </>
  );
};

export default ContractMemberFind;
